import React from 'react';
import { useNode } from '@craftjs/core';
import { PROP_POSICION, normalizarPosicion } from '../utils/posicionLibre';
import { htmlSeguro } from '../utils/htmlSeguro';
import { htmlConTipografiaMovil } from '../utils/tipografiaMovil';

/**
 * El NIVEL y el TAMAÑO son dos cosas distintas.
 *
 * `level` es semántica —qué lugar ocupa este título en la jerarquía de la
 * página, que es lo que leen Google y un lector de pantalla— y se elige en el
 * panel. `fontSize` es cómo se ve, y se ajusta desde la barra del elemento.
 * Agrandar un texto no puede convertir un H2 en H1: son decisiones de distinta
 * naturaleza y mezclarlas rompía el SEO sin que nadie se enterara.
 *
 * Sin `fontSize` manda la clase que le toca al nivel, o sea exactamente lo que
 * se veía antes de que este prop existiera: nada de lo ya guardado cambia.
 */
interface HeadingProps {
  text?: string;
  level?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  fontWeight?: string;
  /** Tamaño visual en px. Ausente = el que le da su nivel. */
  fontSize?: number;
}

/**
 * En un lienzo la caja del título ES el frame.
 *
 * Espejo del editor: el bloque llena el frame y hereda de él la alineación
 * vertical (`justify-content: inherit`, que el envoltorio fija según
 * `_layout.v`), así el alineado se calcula sobre todo el alto y la página
 * publicada se ve igual que el canvas.
 */
/** El bloque llena el frame y hereda de él la alineación vertical. */
const enElLienzo = (enLienzo: boolean): React.CSSProperties | null => (enLienzo
  ? {
    height: '100%',
    margin: 0,
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'inherit',
  }
  : null);

export const Heading = ({
  text = 'Título de sección',
  level = 'h2',
  color = '#111827',
  textAlign = 'left',
  fontWeight = '700',
  fontSize,
}: HeadingProps) => {
  const { connectors, selected, enLienzo } = useNode((state) => ({
    selected: state.events.selected,
    enLienzo: !!normalizarPosicion((state.data.props as Record<string, unknown>)?.[PROP_POSICION]),
  }));

  const sizeMap = {
    h1: 'text-4xl md:text-5xl',
    h2: 'text-3xl md:text-4xl',
    h3: 'text-2xl md:text-3xl',
    h4: 'text-xl md:text-2xl',
    h5: 'text-lg md:text-xl',
    h6: 'text-base md:text-lg',
  };

  const Tag = level;

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        border: selected ? '1px dashed #1e88e5' : '1px solid transparent',
        padding: '4px',
        ...enElLienzo(enLienzo),
      }}
    >
      <Tag
        className={`${sizeMap[level]} leading-tight`}
        style={{
          color,
          textAlign,
          fontWeight,
          margin: 0,
          // En línea le gana a la clase del nivel; ausente, la clase manda.
          ...(Number(fontSize) > 0 ? { fontSize: `${Number(fontSize)}px` } : null),
        }}
        // El título ahora se escribe en el canvas y su contenido viaja como
        // HTML, igual que el del texto: pintarlo plano mostraría las etiquetas.
        dangerouslySetInnerHTML={htmlConTipografiaMovil(htmlSeguro(text || ''))}
      />
    </div>
  );
};

(Heading as any).craft = {
  displayName: 'Heading',
  props: {
    text: 'Título de sección',
    level: 'h2',
    color: '#111827',
    textAlign: 'left',
    fontWeight: '700',
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};
